import { AddButton } from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { useIsTouchDevice } from "client/Hooks";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { RenameLinkedDocumentDialog } from "./RenameLinkedDocumentDialog";
import { InputValidationResultKind } from "client/Validation";
import { HubGridViewLayout } from "client/Features/Hub/HubGridViewLayout/HubGridViewLayout";
import { localize } from "client_legacy/ndaw/culture";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { never } from "client_legacy/ndaw/errors";
import { HubLinkedDocumentSaveModel } from "client/Api/Hub/common.composer";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	getLinkedDocumentId,
	useAddDocuments
} from "../useAddDocuments";
import { LinkedDocumentItem } from "../LinkedDocumentItem";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";
import { LinkedDocumentFormData } from "../common";

export const MAX_LINKED_DOCUMENTS_COUNT: number = 40;

export type LinkedDocumentsProps = {
	attachable: IValueInputAttachable<readonly LinkedDocumentFormData[]>;
	onChangeIsLoading(isLoading: boolean): void;
	isSharepointEnabled: boolean;
	webDavConfiguration: WebDavConfiguration | null;
};

export const LinkedDocuments = (props: LinkedDocumentsProps): ReactElement => {
	const {
		attachable,
		onChangeIsLoading,
		isSharepointEnabled,
		webDavConfiguration
	} = props;

	const [itemToRename, setItemToRename] = useState<ItemToRename | null>(null);

	const items = useMemo(
		(): readonly LinkedDocumentFormData[] => attachable.value,
		[attachable]
	);

	const selectionMadeHandler = useCallback(
		(itemsToAdd: readonly LinkedDocumentFormData[]) => {
			const currentItemsCount: number = items.length;
			const itemsToAddCount: number = itemsToAdd.length;

			if (itemsToAddCount + currentItemsCount > MAX_LINKED_DOCUMENTS_COUNT) {
				throw new Error(`The maximum number of allowed documents is ${MAX_LINKED_DOCUMENTS_COUNT}.`);
			}

			attachable
				.reducer
				.setValue([
					...items,
					...itemsToAdd
				]);

			attachable
				.validation
				.triggerInputFinished();
		},
		[
			items,
			attachable
		]
	);

	const {
		add: addHandler,
		dialogElement,
		openDocument
	} = useAddDocuments({
		isSharepointEnabled,
		items,
		maxItemsCount: MAX_LINKED_DOCUMENTS_COUNT,
		onChangeIsLoading,
		onSelect: selectionMadeHandler,
		webDavConfiguration
	});

	const isTouch: boolean = useIsTouchDevice();
	const canAddMoreItems: boolean = items.length < MAX_LINKED_DOCUMENTS_COUNT;

	const renameHandler = useCallback(
		(
			newName: string,
			index: number
		): void => {
			const currentItems: readonly LinkedDocumentFormData[] = attachable.value;
			const copy: LinkedDocumentFormData[] = [...currentItems];

			if (isNil(copy[index])) {
				throw new Error(`The item with index '${index}' doesn't exist`);
			}

			attachable
				.reducer
				.forIndex(index)
				.setValue({ ...copy[index], name: newName });

			attachable
				.validation
				.triggerInputFinished();

			setItemToRename(null);
		},
		[attachable]
	);

	const removeHandler = useCallback(
		(index: number): void => {
			attachable
				.reducer
				.removeAt(index);

			attachable
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const isWebDavAvailable: boolean = isNotNil(webDavConfiguration)
		&& webDavConfiguration.isConfigured;

	return (
		<>
			<HubGridViewLayout.List trackingId="linked-documents-list">
				{attachable.map((itemHandler, index) => {
					const {
						value
					} = itemHandler;

					const id: string = getLinkedDocumentId(value);

					return (
						<HubGridViewLayout.ListItem
							key={id}
						>
							<ListItem
								attachable={itemHandler}
								onRename={() => setItemToRename({initialName: value.name, index})}
								onRemove={() => removeHandler(index)}
								onClick={() => openDocument(value)}
								isWebDavAvailable={isWebDavAvailable}
								isSharepointEnabled={isSharepointEnabled}
								isTouch={isTouch}
							/>
						</HubGridViewLayout.ListItem>
					);
				})}
				{(canAddMoreItems && addHandler) && (
					<HubGridViewLayout.ListItem>
						<AddButton
							onClick={addHandler}
							trackingId="add-button"
						/>
					</HubGridViewLayout.ListItem>
				)}
			</HubGridViewLayout.List>
			{itemToRename && (
				<RenameLinkedDocumentDialog
					initialName={itemToRename.initialName}
					onClose={() => setItemToRename(null)}
					onRenamed={(newName) => renameHandler(newName, itemToRename.index)}
				/>
			)}
			{dialogElement}
		</>
	);
};
LinkedDocuments.displayName = "LinkedDocuments";

type ItemToRename = {
	initialName: string;
	index: number;
};

type ListItemProps = {
	attachable: IValueInputAttachable<LinkedDocumentFormData>;
	isWebDavAvailable: boolean;
	isSharepointEnabled: boolean;
	isTouch: boolean;
	onRename(): void;
	onRemove(): void;
	onClick(): void;
};

const ListItem = (props: ListItemProps): ReactElement => {
	const {
		attachable,
		isWebDavAvailable,
		isSharepointEnabled,
		isTouch,
		onRename,
		onRemove,
		onClick
	} = props;

	const actions = useMemo(
		(): readonly Action<LinkedDocumentFormData>[] => getActions(
			onRename,
			onRemove
		),
		[
			onRemove,
			onRename
		]
	);

	const hasError = useMemo(
		(): boolean => attachable.validation.getValidationState().kind === InputValidationResultKind.Invalid,
		[attachable.validation]
	);

	return (
		<LinkedDocumentItem
			model={attachable.value}
			actionContext={attachable.value}
			actions={actions}
			isSharepointEnabled={isSharepointEnabled}
			isWebDavAvailable={isWebDavAvailable}
			hasError={hasError}
			isTouch={isTouch}
			onClick={onClick}
		/>
	);
};
ListItem.displayName = "ListItem";

function getActions(
	onRename: (item: LinkedDocumentFormData) => void,
	onRemove: () => void
): readonly Action<LinkedDocumentFormData>[] {
	return ([
		{
			kind: ActionKind.Regular,
			name: "renameLink",
			title: localize("_RenameLink"),
			canExecute: _ => true,
			execute: onRename,
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "removeLink",
			title: localize("_RemoveLink"),
			canExecute: _ => true,
			execute: _ => onRemove(),
			icon: "trash-can"
		}
	]);
};

export function convertToLinkedDocumentSaveModel(
	item: LinkedDocumentFormData
): HubLinkedDocumentSaveModel {
	switch(item.documentStoreType) {
		case DocumentStoreType.SharePoint:
			return {
				documentStoreType: item.documentStoreType,
				itemId: item.itemId,
				location: item.location,
				name: item.name
			};
		case DocumentStoreType.WebDav:
			return {
				documentStoreType: item.documentStoreType,
				fileId: item.fileId,
				name: item.name,
				serverId: item.serverId
			};
		default:
			never(item);
	}
}

export function convertToLinkedDocumentFormData(
	item: HubLinkedDocumentViewModel
): LinkedDocumentFormData {
	switch (item.documentStoreType) {
		case DocumentStoreType.SharePoint:
			return {
				documentStoreType: DocumentStoreType.SharePoint,
				itemId: item.itemId,
				location: item.location,
				name: item.name,
				extension: item.extension
			};
		case DocumentStoreType.WebDav:
			return {
				documentStoreType: DocumentStoreType.WebDav,
				fileId: item.fileId,
				serverId: item.serverId,
				name: item.name,
				extension: item.extension
			};
		default:
			never(item);
	}
}
